/**
 * IM-004: Wraps Commerce SDK `useProducts` and injects `inventoryIds` from the warehouse cookie.
 */

import {useMemo} from 'react';

import {useProducts as useBaseProducts} from '@salesforce/commerce-sdk-react';

import {mergeCommerceSdkProductOptionsWithInventoryIds} from '../utils/merge-commerce-sdk-product-options';
import {useShopperInventoryIds} from './use-shopper-inventory-ids';

/**
 * KL-1028 — minimal SCAPI `expand` for checkout product lookups.
 *
 * Checkout only needs stock (`availability`), a thumbnail fallback (`images`),
 * price parity/deposit math (`prices`) and KingGuard/option rows (`options`).
 * It deliberately omits `variations` (and set/bundle/links/recommendations),
 * which are the bulk of a complex master's payload and are never read on
 * checkout (line rendering uses the persisted `c_selectedOptionsUI`, not the
 * variation tree). See docs/performance/checkout-optimization.md (Finding 2).
 */
export const CHECKOUT_PRODUCT_EXPAND = ['availability', 'images', 'prices', 'options'];

/**
 * @param {Object} options - Commerce SDK `useProducts` options
 * @param {Object} [queryOptions] - React Query options
 * @param {Object} [config]
 * @param {string|undefined} [config.inventoryIds] - When provided, overrides cookie-based ids
 * @param {'checkout'} [config.mode] - When `'checkout'` and no explicit `expand` is
 *   supplied, injects {@link CHECKOUT_PRODUCT_EXPAND} to shrink the payload. Inert otherwise.
 */
export function useProducts(options, queryOptions, config) {
  const cookieIds = useShopperInventoryIds();
  const inventoryIds =
    config && Object.prototype.hasOwnProperty.call(config, 'inventoryIds') ?
      config.inventoryIds :
      cookieIds;
  const mode = config?.mode;
  const scopedOptions = useMemo(() => {
    if (mode !== 'checkout' || options?.parameters?.expand) {
      return options;
    }

    return {
      ...options,
      parameters: {...options?.parameters, expand: CHECKOUT_PRODUCT_EXPAND},
    };
  }, [mode, options]);
  const mergedOptions = useMemo(
    () => mergeCommerceSdkProductOptionsWithInventoryIds(scopedOptions, inventoryIds),
    [scopedOptions, inventoryIds]
  );

  return useBaseProducts(mergedOptions, queryOptions);
}

export default useProducts;
